import React, { useState } from "react";
import api from "../api/axios";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPills, FaCheck, FaTimes } from "react-icons/fa";
import { useTranslation } from "../context/LanguageContext";
import "../styles/common.css";
import "./AjouterProduitPharmaceutique.css";


export default function AjouterProduitPharmaceutique() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const TYPE_PRODUIT_OPTIONS = React.useMemo(() => [
    { value: 'vaccin', label: t.vaccine },
    { value: 'vermifuge', label: t.dewormer },
    { value: 'antibiotique', label: t.antibiotic },
    { value: 'vitamine', label: t.vitamin },
    { value: 'desinfectant', label: t.disinfectant },
    { value: 'autre', label: t.other },
  ], [t]);

  const UNITE_OPTIONS = React.useMemo(() => [
    { value: 'ml', label: 'ml' },
    { value: 'comprime', label: t.tablet },
    { value: 'flacon', label: t.bottle },
    { value: 'dose', label: t.dose },
    { value: 'kg', label: 'kg' },
    { value: 'g', label: 'g' },
    { value: 'unite', label: t.unit },
  ], [t]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    nom: "",
    type_produit: "",
    unite: "",
    description: "",
    actif: true,
  });

  const [fieldErrors, setFieldErrors] = useState({
    nom: "",
    type_produit: "",
    unite: "",
  });

  const [touchedFields, setTouchedFields] = useState({
    nom: false,
    type_produit: false,
    unite: false,
  });

  const validateRequiredField = (name, value) => {
    if (!value || (typeof value === 'string' && value.trim() === "") || value === null) {
      return "Ce champ est obligatoire";
    }
    return "";
  };

  const validateField = (name, value) => {
    if (name === "nom" || name === "type_produit" || name === "unite") {
      return validateRequiredField(name, value);
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;

    setFormData({
      ...formData,
      [name]: fieldValue,
    });

    if ((name === "nom" || name === "type_produit" || name === "unite") && (touchedFields[name] || fieldErrors[name])) {
      const error = validateField(name, fieldValue);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    if (touchedFields.hasOwnProperty(name)) {
      setTouchedFields(prev => ({
        ...prev,
        [name]: true
      }));
    }

    if (name === "nom" || name === "type_produit" || name === "unite") {
      const error = validateField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newTouchedFields = {
      nom: true,
      type_produit: true,
      unite: true,
    };
    setTouchedFields(newTouchedFields);

    const newFieldErrors = {
      nom: validateField("nom", formData.nom),
      type_produit: validateField("type_produit", formData.type_produit),
      unite: validateField("unite", formData.unite),
    };
    setFieldErrors(newFieldErrors);

    const hasErrors = Object.values(newFieldErrors).some(error => error !== "");

    if (hasErrors) {
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setLoading(true);
    try {
      await api.post("/api/produits-pharmaceutiques/", formData);
      setShowConfirmModal(false);
      toast.success("Produit pharmaceutique ajouté avec succès !");
      setTimeout(() => {
        navigate("/produits-pharmaceutiques");
      }, 500);
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.detail ||
        err.response?.data?.message ||
        Object.values(err.response?.data || {}).flat().join(", ") ||
        "Erreur lors de l'ajout du produit !";
      toast.error(errorMessage);
      setShowConfirmModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  const isFormValid = () => {
    if (!formData.nom || !formData.nom.trim()) return false;
    if (!formData.type_produit || !formData.type_produit.trim()) return false;
    if (!formData.unite || !formData.unite.trim()) return false;
    return true;
  };

  return (
    <div className="ajouter-produit-pharmaceutique-page">
      <div className="title-badge">
        <FaPills className="page-title-icon" aria-hidden="true" />
        <span>{t.addProductTitle}</span>
      </div>

      <form onSubmit={handleSubmit} className="ajouter-produit-form">
        <div className="form-info-banner">
          <span className="info-icon">ℹ️</span>
          <div className="info-content">
            <strong>{t.requiredFields}</strong>
            <span className="required-indicator">*</span> {t.requiredFieldNote}
          </div>
        </div>

        <div className="form-section-container">
          <div className="form">
            <label htmlFor="nom">
              {t.productNameLabel} <span className="required-indicator">*</span>
            </label>
            <input
              type="text"
              id="nom"
              name="nom"
              placeholder={t.productNameLabel}
              value={formData.nom}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.nom && touchedFields.nom ? "input-error" : ""}
              maxLength={191}
            />
            {fieldErrors.nom && touchedFields.nom && (
              <span className="field-error-message">{fieldErrors.nom}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="type_produit">
              {t.productTypeLabel} <span className="required-indicator">*</span>
            </label>
            <select
              id="type_produit"
              name="type_produit"
              value={formData.type_produit}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.type_produit && touchedFields.type_produit ? "input-error" : ""}
            >
              <option value="">{t.selectType}</option>
              {TYPE_PRODUIT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {fieldErrors.type_produit && touchedFields.type_produit && (
              <span className="field-error-message">{fieldErrors.type_produit}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="unite">
              {t.unitLabel} <span className="required-indicator">*</span>
            </label>
            <select
              id="unite"
              name="unite"
              value={formData.unite}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.unite && touchedFields.unite ? "input-error" : ""}
            >
              <option value="">{t.selectUnit}</option>
              {UNITE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {fieldErrors.unite && touchedFields.unite && (
              <span className="field-error-message">{fieldErrors.unite}</span>
            )}
          </div>

          <div className="form full-width">
            <label htmlFor="description">{t.description}</label>
            <textarea
              id="description"
              name="description"
              placeholder={t.productDescriptionOptional}
              value={formData.description}
              onChange={handleChange}
              rows="4"
            />
          </div>

          <div className="form">
            <label htmlFor="actif" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                id="actif"
                name="actif"
                checked={formData.actif || false}
                onChange={handleChange}
              />
              <span>{t.activeProduct}</span>
            </label>
          </div>
        </div>

        <div className="actions">
          <button
            type="submit"
            className="save-but"
            disabled={!isFormValid() || loading}
          >
            <FaCheck />
            <span>{loading ? t.registering : t.register}</span>
          </button>
          <button
            type="button"
            className="cancel-but"
            onClick={() => navigate("/produits-pharmaceutiques")}
            disabled={loading}
          >
            <FaTimes />
            <span>{t.cancel}</span>
          </button>
        </div>
      </form>

      {/* Modal de confirmation */}
      {showConfirmModal && (
        <div className="confirm-modal-overlay" onClick={handleCancelConfirm}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <FaCheck className="confirm-modal-icon" />
              <h3>Confirmer l'ajout</h3>
            </div>
            <div className="confirm-modal-body">
              <p className="confirm-intro">
                Veuillez vérifier les informations avant de confirmer l'ajout :
              </p>
              <div className="confirm-summary">
                <div className="summary-row">
                  <span className="summary-label">Nom :</span>
                  <span className="summary-value">{formData.nom}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Type :</span>
                  <span className="summary-value">{TYPE_PRODUIT_OPTIONS.find(opt => opt.value === formData.type_produit)?.label || formData.type_produit}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Unité :</span>
                  <span className="summary-value">{UNITE_OPTIONS.find(opt => opt.value === formData.unite)?.label || formData.unite}</span>
                </div>
              </div>
            </div>
            <div className="confirm-modal-footer">
              <button
                type="button"
                onClick={handleCancelConfirm}
                className="confirm-modal-cancel"
                disabled={loading}
              >
                <FaTimes />
                <span>{t.cancel}</span>
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                className="confirm-modal-confirm"
                disabled={loading}
              >
                <FaCheck />
                <span>{loading ? "Enregistrement..." : "Confirmer"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}